import React, { useState, useMemo, useEffect } from 'react';
import { Select, Empty } from 'antd';
import { isEmpty, startCase } from 'lodash';
import moment from 'moment-timezone';
import DualAxisAreaChart from '../../../../shared/designSystem/components/DualAxisAreaChart';
import SingleAxisBarChart from '../../../../shared/designSystem/components/SingleAxisBarChart';
import { isDayOfWeekString, isUnixTimestamp } from '../../../../../core/utils/campaigns';
import { MenuItemTextColor } from '../../../../shared/globalStyling/styledText';

const { Option } = Select;

// =============================================================================
// CampaignOverviewChart — Display tab chart, unified card body.
//
// Segmented toggle picks one of three views that Display users had in v1:
//   - "By Date"   → time series, dual-axis area (Impressions × Clicks/CTR)
//   - "By Day"    → day-of-week aggregate, dual-axis area
//   - "Device"    → device type, single-axis bar
// In v1 these lived in AntD Tabs inside a separate Card. Here they collapse
// into a single body that swaps the renderer based on mode, with the page-
// level date range picker owning the "By Date" filter from the card header.
// =============================================================================

// Monday-first (ISO 8601). Sort order for the "By Day" chart — kept distinct
// from v1's Sunday-first OverviewTab map because the v1 sort was effectively
// a no-op (it keyed off moment objects, not strings) so the chart rendered
// in whatever order the backend returned. We render in ISO order explicitly.
const ORDERED_DAYS = {
  Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
  Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6,
};

const MODES = [
  { key: 'byDate', label: 'By Date' },
  { key: 'byDay',  label: 'By Day' },
  { key: 'device', label: 'Device' },
];

const CampaignOverviewChart = ({
  chartData,
  dataCampaignRangeDate,
  chartsBuilt,
  loading = false,
  // Authoritative "the report is settled" signal. Without it this component can
  // only INFER loading from whether any series has rows, which makes a genuinely
  // empty dataset indistinguishable from one still in flight (see the
  // hasAnyFetchedData block below). Defaults false, preserving prior behavior.
  dataSettled = false,
}) => {
  const [mode, setMode] = useState('byDate');
  const [leftY, setLeftY] = useState('Impressions');
  const [rightY, setRightY] = useState('Clicks');

  // Normalize incoming shapes once — parity with OverviewTab's try/catch.
  const { byDate, byDay, deviceType } = useMemo(() => {
    if (!chartData) return { byDate: [], byDay: [], deviceType: [] };
    const rawByDate = (chartData.byDate || []).map((d) => ({
      ...d,
      xAxis: isUnixTimestamp(d.xAxis)
        ? moment.unix(d.xAxis).utc().format('YYYY-MM-DD')
        : moment(new Date(d.xAxis)).utc().format('YYYY-MM-DD'),
    }));
    let rawByDay = chartData.byDay || [];
    if (!isEmpty(rawByDay)) {
      rawByDay = rawByDay
        .map((day) => ({
          ...day,
          xAxis: isDayOfWeekString(day.xAxis) ? moment().isoWeekday(day.xAxis).format('dddd') : day.xAxis,
        }))
        .sort((a, b) => (ORDERED_DAYS[a.xAxis] ?? 0) - (ORDERED_DAYS[b.xAxis] ?? 0));
    }
    const rawDevice = (chartData.deviceType || []).map((d) =>
      Object.keys(d).includes('xAxis') ? d : { ...d, xAxis: 'Unknown' }
    );
    return { byDate: rawByDate, byDay: rawByDay, deviceType: rawDevice };
  }, [chartData]);

  // Data the active chart actually renders.
  const activeData = useMemo(() => {
    if (mode === 'byDate') {
      if (!isEmpty(dataCampaignRangeDate)) return dataCampaignRangeDate;
      return byDate.slice().sort((a, b) => (moment(a.xAxis).isBefore(b.xAxis) ? -1 : 1));
    }
    if (mode === 'byDay') return byDay;
    return deviceType;
  }, [mode, byDate, byDay, deviceType, dataCampaignRangeDate]);

  // Axis options — device uses a reduced key set, no right axis.
  const validOptions = useMemo(() => {
    if (mode === 'device') return ['clicks', 'impressions', 'ctr'];
    return ['clicks', 'impressions', 'ctr'];
  }, [mode]);

  const axisKeys = useMemo(() => {
    if (isEmpty(activeData) || !activeData[0]) return [];
    return Object.keys(activeData[0])
      .filter((el) => validOptions.includes(el))
      .map((option) =>
        option === 'cpc' || option === 'cpm' || option === 'ctr' || option === 'ecpm'
          ? option.toUpperCase()
          : startCase(option)
      )
      .filter((el) => el !== 'Creativeurl');
  }, [activeData, validOptions]);

  const availableModes = MODES.filter((m) => {
    if (m.key === 'byDate') return !isEmpty(byDate);
    if (m.key === 'byDay') return !isEmpty(byDay);
    if (m.key === 'device') return !isEmpty(deviceType);
    return false;
  });

  // `mode` is component state and survives a chartData swap. Since the Display
  // tab now feeds a per-provider slice, switching to a campaign that lacks the
  // active mode's series would leave the body blank with no mode highlighted.
  // Fall back to the first mode the new data actually offers.
  useEffect(() => {
    if (isEmpty(availableModes)) return;
    if (availableModes.some((m) => m.key === mode)) return;
    setMode(availableModes[0].key);
  }, [availableModes, mode]);

  if (isEmpty(availableModes)) {
    // Data-state branch: distinguish "still fetching" from "truly empty."
    //
    // Parent (`CampaignChartsController`) initializes `chartData` as `{}`
    // and a pre-fetch useEffect can pre-seed `chartData.byDate = []` via
    // `prepareTransactions()` BEFORE the real fetch completes. That
    // creates a window where:
    //   · `loading` has flipped false (fetch settled)
    //   · `chartData.byDate` exists as `Array.isArray === true` but is
    //      empty from the pre-seed
    //   · the real setChartData call from `rawAttributionData`'s useEffect
    //     hasn't committed yet
    // During that window, a weaker check ("does any array exist") would
    // return true and fall through to the Empty state — exactly the
    // "No chart data yet" flash the user reports.
    //
    // Stricter signal: require at least one of the three chart-key arrays
    // to have NON-ZERO length. Empty arrays are treated as not-yet-loaded.
    //
    // The "data settled" flag this comment used to defer is now the
    // `dataSettled` prop. When the caller can prove the report resolved and the
    // selection is genuinely empty (e.g. a zero-delivery provider tab), it says
    // so and we fall through to the Empty state instead of shimmering forever.
    // Callers that cannot prove it omit the prop and keep the old inference.
    const hasAnyFetchedData =
      chartData &&
      ((Array.isArray(chartData.byDate) && chartData.byDate.length > 0) ||
        (Array.isArray(chartData.byDay) && chartData.byDay.length > 0) ||
        (Array.isArray(chartData.deviceType) && chartData.deviceType.length > 0));
    const isStillLoading = loading || !chartData || (!hasAnyFetchedData && !dataSettled);
    if (isStillLoading) {
      return (
        <div
          className="campaign-overview__chart"
          style={{ padding: '16px 20px 24px' }}
        >
          <span
            className="skeleton"
            style={{ width: 120, height: 12, borderRadius: 4, marginBottom: 20 }}
          />
          <span
            className="skeleton"
            style={{ width: '100%', height: 320, borderRadius: 12 }}
          />
        </div>
      );
    }
    return (
      <div
        className="campaign-overview__chart"
        style={{ padding: '32px 20px', display: 'flex', justifyContent: 'center' }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data yet" />
      </div>
    );
  }

  const showRightAxis = mode !== 'device';

  return (
    <div className="campaign-overview__chart">
      {/* Head row: Trend eyebrow + axis selectors inline — same layout as
          Summary tab so both read identically. Mode toggle (By Date/By Day/
          Device) lives BELOW the head as a sub-nav, since it switches what
          the chart is slicing rather than changing which metrics it shows. */}
      <div className="campaign-overview__chart-head">
        <div className="campaign-overview__chart-eyebrow mono-eyebrow">
          Trend
          {chartsBuilt && (
            <span style={{ marginLeft: 8, fontSize: 10, opacity: 0.7 }}>
              Updated {moment(chartsBuilt).calendar()}
            </span>
          )}
        </div>

        {!isEmpty(activeData) && axisKeys.length > 0 && (
          <div className="campaign-overview__axis-row">
            <div className="campaign-overview__axis-field">
              <span className="campaign-overview__axis-label mono-eyebrow">
                <span className="campaign-overview__axis-dot campaign-overview__axis-dot--left" />
                Left axis
              </span>
              <Select
                onSelect={(value) => setLeftY(value)}
                className="campaign-overview__axis-select"
                value={leftY}
                getPopupContainer={(trigger) => trigger.parentNode}
                popupClassName="mjl-ds"
              >
                {axisKeys
                  .filter((op) => !showRightAxis || op !== rightY)
                  .map((option) => (
                    <Option key={option} value={option}>
                      <MenuItemTextColor text={option} color={'#7dafff'} />
                    </Option>
                  ))}
              </Select>
            </div>

            {showRightAxis && (
              <div className="campaign-overview__axis-field campaign-overview__axis-field--right">
                <span className="campaign-overview__axis-label mono-eyebrow">
                  <span className="campaign-overview__axis-dot campaign-overview__axis-dot--right" />
                  Right axis
                </span>
                <Select
                  onSelect={(value) => setRightY(value)}
                  className="campaign-overview__axis-select"
                  value={rightY}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  popupClassName="mjl-ds"
                >
                  {axisKeys
                    .filter((op) => op !== leftY)
                    .map((option) => (
                      <Option key={option} value={option}>
                        <MenuItemTextColor text={option} color={'#92d5a7'} />
                      </Option>
                    ))}
                </Select>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="campaign-overview__mode-row">
        <div className="campaign-overview__mode segmented-tabs" role="tablist" aria-label="Chart mode">
          {availableModes.map((m) => (
            <button
              key={m.key}
              role="tab"
              type="button"
              aria-selected={mode === m.key}
              onClick={() => setMode(m.key)}
              className={
                'segmented-tabs__btn' +
                (mode === m.key ? ' segmented-tabs__btn--active' : '')
              }
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="campaign-overview__chart-body">
        {!isEmpty(activeData) && mode !== 'device' && (
          <DualAxisAreaChart
            leftY={isEmpty(leftY) ? validOptions[0] : leftY}
            rightY={isEmpty(rightY) ? validOptions[1] : rightY}
            dualChartData={activeData}
          />
        )}
        {!isEmpty(activeData) && mode === 'device' && (
          <SingleAxisBarChart
            leftY={isEmpty(leftY) ? validOptions[0] : leftY}
            data={activeData}
          />
        )}
      </div>
    </div>
  );
};

export default CampaignOverviewChart;
